import { useReducer } from 'react';
import { z } from 'zod';

const emailSchema = z.email();

type State = {
	value: string;
	isError: boolean;
	isBlurred: boolean;
};

type Action =
	| { type: 'SET_EMAIL'; payload: string }
	| { type: 'SET_IS_EMAIL_ERROR'; payload: boolean }
	| { type: 'SET_BLURRED'; payload: boolean }
	| { type: 'RESET' };

const initialState = (value = '') => ({
	value,
	isError: false,
	isBlurred: false,
});

type EmailInputProps = {
	state: State;
	handleEmailChange: (value: string) => void;
	handleEmailValidation: () => void;
};

const reducer = (state: State, action: Action): State => {
	switch (action.type) {
		case 'SET_EMAIL': {
			return { ...state, value: action.payload };
		}
		case 'SET_IS_EMAIL_ERROR': {
			return { ...state, isError: action.payload };
		}
		case 'SET_BLURRED': {
			return { ...state, isBlurred: action.payload };
		}
		case 'RESET': {
			return initialState();
		}
		default: {
			return state;
		}
	}
};

export const useEmailInput = (value = ''): EmailInputProps => {
	const [state, dispatch] = useReducer(reducer, initialState(value));

	const handleEmailChange = (value: string) => {
		dispatch({ type: 'SET_BLURRED', payload: false });
		dispatch({ type: 'SET_EMAIL', payload: value });

		const emailIsValid = emailSchema.safeParse(value).success;
		if (emailIsValid) {
			dispatch({ type: 'SET_IS_EMAIL_ERROR', payload: false });
		} else if (state.isBlurred) {
			dispatch({ type: 'SET_IS_EMAIL_ERROR', payload: true });
		}
	};

	const handleEmailValidation = () => {
		dispatch({ type: 'SET_BLURRED', payload: true });
		if (state.value.length > 0) {
			const isEmailError = emailSchema.safeParse(state.value).success;
			dispatch({ type: 'SET_IS_EMAIL_ERROR', payload: !isEmailError });
		} else {
			dispatch({ type: 'SET_IS_EMAIL_ERROR', payload: false });
		}
	};

	return { state, handleEmailChange, handleEmailValidation };
};
